import React from "react";
import { motion } from "framer-motion";
import { ChevronDown, Snowflake, ArrowRight } from "lucide-react";
import { usePerformance } from "../../hooks/usePerformance";

export const Hero = () => {
  const { shouldReduceMotion } = usePerformance();
  const dur = shouldReduceMotion ? 0 : 0.45;

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen-safe w-full overflow-hidden flex items-center"
    >
      {/* Background image — full bleed */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="/team/team-completo.jpg?v=2"
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "65% 45%",
          }}
        />
      </div>

      {/* Overlay 1 — gradient azul marca */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(4,37,72,0.85) 0%, rgba(12,65,152,0.75) 50%, rgba(4,37,72,0.65) 100%)",
        }}
      />

      {/* Overlay 2 — viñeta radial */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(4,37,72,0.4) 0%, rgba(4,37,72,0.7) 100%)",
        }}
      />

      {/* Overlay 3 — textura sutil de puntos para romper planicie */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
          opacity: 0.4,
        }}
      />

      {/* Large subtle circle for depth */}
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none hidden md:block"
        style={{ background: "rgba(0,183,224,0.05)" }}
      />

      {/* Content — centered */}
      <div className="relative z-10 w-full px-6 md:px-12">
        <div className="max-w-[900px] mx-auto text-center flex flex-col items-center">
          {/* Pre-title with icon */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: dur }}
          >
            <span
              className="inline-flex items-center gap-2 text-jr-cyan font-semibold uppercase mb-6"
              style={{ fontSize: "14px", letterSpacing: "3px" }}
            >
              <Snowflake size={14} className="text-jr-cyan" />
              Climatización profesional
            </span>
            <div className="mx-auto w-[60px] h-[3px] bg-jr-cyan rounded-full" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: shouldReduceMotion ? 0 : 0.15, duration: dur }}
            className="mt-6 font-display font-bold text-white leading-[1.1] tracking-[-0.02em]"
            style={{
              fontSize: "clamp(3rem, 6vw, 5.5rem)",
              textShadow: "0 4px 20px rgba(0,0,0,0.3)",
            }}
            data-testid="hero-title"
          >
            ¡El clima{" "}
            <span
              className="text-jr-cyan"
              style={{
                fontWeight: 800,
                textShadow: "0 0 40px rgba(0,183,224,0.4), 0 4px 20px rgba(0,0,0,0.3)",
              }}
            >
              perfecto
            </span>
            ,
            <br className="hidden sm:block" />
            siempre a tu alcance!
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: shouldReduceMotion ? 0 : 0.25, duration: dur }}
            className="mx-auto mt-6 mb-10 font-light"
            style={{
              color: "rgba(255,255,255,0.9)",
              fontSize: "clamp(1.125rem, 1.5vw, 1.375rem)",
              maxWidth: "700px",
              lineHeight: 1.6,
            }}
            data-testid="hero-subtitle"
          >
            Instalación, mantenimiento y reparación de sistemas de aire
            acondicionado para hogares y negocios. Confort, eficiencia y
            tecnología en cada servicio.
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: shouldReduceMotion ? 0 : 0.35, duration: dur }}
            className="flex flex-col sm:flex-row gap-3 justify-center"
          >
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#contacto")?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-lg bg-jr-cyan text-jr-oxford text-[14px] font-semibold uppercase tracking-[0.08em] hover:bg-jr-yale hover:text-white hover:-translate-y-0.5 transition-all duration-300 min-h-[52px]"
              style={{ boxShadow: "0 8px 30px -6px rgba(0,183,224,0.5)" }}
            >
              Solicitar servicio
              <ArrowRight size={16} />
            </a>
            <a
              href="#nosotros"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector("#nosotros")?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-lg border-2 border-white/40 text-white text-[14px] font-medium uppercase tracking-[0.08em] hover:bg-white/10 hover:-translate-y-0.5 transition-all duration-300 min-h-[52px]"
            >
              Conocer más
              <ChevronDown size={16} />
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator — refined */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: shouldReduceMotion ? 0 : 1, duration: dur }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10"
      >
        <span
          className="text-white/60 text-[11px] font-medium uppercase"
          style={{ letterSpacing: "5px" }}
        >
          Scroll
        </span>
        {/* Animated line */}
        <div className="relative w-[2px] h-[40px] bg-white/15 rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full h-[16px] bg-jr-cyan rounded-full"
            style={{
              animation: "scrollLine 1.8s ease-in-out infinite",
            }}
          />
        </div>
      </motion.div>

      {/* Keyframe for scroll line animation */}
      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-4px); opacity: 0; }
          30% { opacity: 1; }
          100% { transform: translateY(28px); opacity: 0; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
